import React, { useEffect, useMemo, useState } from 'react';
import { CountryLabel } from '../../../shared/components/CountryFlag';
import {
    Button, Card, Col, Collapse, Empty, Form, Input, Radio, Row, Select, Space, Spin, Typography, message, theme,
} from 'antd';
import { CheckCircleFilled, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../../shared/i18n';
import PageHeader from '../../../shared/components/PageHeader';
import { userPath } from '../../../shared/config/paths';
import { formatMoney, PLATFORM_CURRENCY } from '../../../shared/utils/money';
import { containerUserApi as api, isOk, errMsg } from '../../../shared/services/containerService';
import { specText, arr } from '../../../shared/components/container/ContainerParts';

const { Text } = Typography;
const CYCLES = { monthly: 'Monthly billing', quarterly: 'Quarterly', annually: 'Yearly' };
const money = (p, cur) => formatMoney(Number(p || 0), cur || PLATFORM_CURRENCY);

const PlanCard = ({ plan, active, onClick }) => {
    const { token } = theme.useToken();
    const from = arr(plan.pricing)[0];
    return (
        <div role="button" tabIndex={0} onClick={onClick}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick(); }}
            style={{
                position: 'relative', cursor: 'pointer', padding: 12, height: '100%', borderRadius: token.borderRadiusLG,
                border: `1px solid ${active ? token.colorPrimary : token.colorBorder}`,
                background: active ? token.colorPrimaryBg : token.colorBgContainer,
            }}>
            {active && <CheckCircleFilled style={{ position: 'absolute', top: 8, right: 8, color: token.colorPrimary }} />}
            <div style={{ fontWeight: 500 }}>{plan.name}</div>
            <Text type="secondary" style={{ fontSize: 12 }}>{specText(plan.spec || {})}</Text>
            {from && <div style={{ marginTop: 4 }}>{money(from.price, from.currency)}</div>}
        </div>
    );
};

const TplCard = ({ title, sub, active, onClick }) => {
    const { token } = theme.useToken();
    return (
        <div role="button" tabIndex={0} onClick={onClick}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick(); }}
            style={{
                cursor: 'pointer', padding: '8px 12px', height: '100%', borderRadius: token.borderRadius,
                border: `1px solid ${active ? token.colorPrimary : token.colorBorder}`,
                background: active ? token.colorPrimaryBg : token.colorBgContainer,
            }}>
            <div style={{ fontWeight: 500 }}>{title}</div>
            <Text type="secondary" ellipsis style={{ fontSize: 12, display: 'block' }}>{sub}</Text>
        </div>
    );
};

const CreateContainer = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [plans, setPlans] = useState([]);
    const [areas, setAreas] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [tplKey, setTplKey] = useState('');
    const productId = Form.useWatch('product_id', form);
    const tpl = useMemo(() => templates.find((x) => x.key === tplKey), [templates, tplKey]);

    useEffect(() => {
        api.templates().then((r) => { if (isOk(r)) setTemplates(arr(r.data?.list)); });
    }, []);

    const pickTpl = (x) => {
        setTplKey(x ? x.key : '');
        const env = x && x.env && typeof x.env === 'object' && !Array.isArray(x.env)
            ? Object.entries(x.env).map(([key, value]) => ({ key, value: value == null ? '' : String(value) }))
            : [];
        form.setFieldsValue({ image: x ? x.image : '', env });
    };

    useEffect(() => {
        api.catalog().then((r) => {
            setLoading(false);
            if (!isOk(r)) { message.error(errMsg(r, t('Failed to load'))); return; }
            const ps = arr(r.data?.plans);
            const as = arr(r.data?.areas);
            setPlans(ps);
            setAreas(as);
            form.setFieldsValue({
                area_id: as[0]?.id,
                product_id: ps[0]?.id,
                cycle: arr(ps[0]?.pricing)[0]?.cycle,
            });
        });
    }, [form, t]);

    const plan = useMemo(() => plans.find((p) => p.id === productId), [plans, productId]);
    const pricing = arr(plan?.pricing);

    const pickPlan = (p) => {
        const cur = form.getFieldValue('cycle');
        const ps = arr(p.pricing);
        form.setFieldsValue({ product_id: p.id, cycle: ps.some((x) => x.cycle === cur) ? cur : ps[0]?.cycle });
    };

    const submit = async (v) => {
        const env = {};
        arr(v.env).forEach((e) => { if (e?.key) env[e.key.trim()] = e.value ?? ''; });
        setSubmitting(true);
        const r = await api.order({
            product_id: v.product_id, cycle: v.cycle, area_id: v.area_id,
            image: v.image.trim(), label: v.label || '', cmd: v.cmd || '', env,
            ...(tpl ? { template: tpl.key } : {}),
        });
        setSubmitting(false);
        if (!isOk(r)) { message.error(errMsg(r, t('Failed to place order'))); return; }
        const d = r.data || {};
        if (d.invoice_id && !d.free) navigate(userPath(`/billing/invoices/${d.invoice_id}`));
        else navigate(userPath('/containers'));
    };

    const back = () => navigate(userPath('/containers'));
    if (loading) return <div style={{ textAlign: 'center', padding: 60 }}><Spin /></div>;

    return (
        <div>
            <PageHeader title={t('Create dedicated container')} onBack={back} />
            {(!plans.length || !areas.length) ? (
                <Card><Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No plans for sale')} /></Card>
            ) : (
                <Card>
                    <Form form={form} layout="vertical" onFinish={submit} style={{ maxWidth: 760 }}>
                        <Form.Item name="area_id" label={t('Region')} rules={[{ required: true }]}>
                            <Select options={areas.map((a) => ({ value: a.id, label: <CountryLabel code={a.code || a.remark}>{a.name}</CountryLabel> }))} style={{ maxWidth: 320 }} />
                        </Form.Item>
                        <Form.Item name="product_id" hidden rules={[{ required: true }]}><Input /></Form.Item>
                        <Form.Item label={t('Plan')} required>
                            <Row gutter={[12, 12]}>
                                {plans.map((p) => (
                                    <Col key={p.id} xs={24} sm={12} md={8}>
                                        <PlanCard plan={p} active={p.id === productId} onClick={() => pickPlan(p)} />
                                    </Col>
                                ))}
                            </Row>
                        </Form.Item>
                        <Form.Item name="cycle" label={t('Billing cycle')} rules={[{ required: true }]}>
                            <Radio.Group optionType="button" options={pricing.map((c) => ({
                                value: c.cycle,
                                label: `${t(CYCLES[c.cycle] || c.cycle)} ${money(c.price, c.currency)}`,
                            }))} />
                        </Form.Item>
                        {templates.length > 0 && (
                            <Form.Item label={t('App')}>
                                <Row gutter={[8, 8]}>
                                    <Col xs={12} sm={8} md={6}>
                                        <TplCard title={t('Custom image')} sub={t('Any image')} active={!tpl} onClick={() => pickTpl(null)} />
                                    </Col>
                                    {templates.map((x) => (
                                        <Col key={x.key} xs={12} sm={8} md={6}>
                                            <TplCard title={x.name || x.key} sub={x.image} active={tplKey === x.key} onClick={() => pickTpl(x)} />
                                        </Col>
                                    ))}
                                </Row>
                            </Form.Item>
                        )}
                        <Form.Item name="image" label={t('Image')} rules={[{ required: true, whitespace: true, message: t('Enter an image') }]}
                            extra={tpl && arr(tpl.secrets).length > 0 ? t('Passwords and other secrets are generated automatically') : undefined}>
                            <Input placeholder="nginx:1.27-alpine" readOnly={!!tpl} />
                        </Form.Item>
                        <Form.Item name="label" label={t('Name')}>
                            <Input placeholder={t('Optional')} maxLength={64} />
                        </Form.Item>
                        <Collapse ghost style={{ marginBottom: 16 }} items={[{
                            key: 'adv', label: t('Advanced'), forceRender: true, children: (
                                <>
                                    <Form.Item name="cmd" label={t('Start command')}>
                                        <Input placeholder={t('Optional')} />
                                    </Form.Item>
                                    <Form.Item label={t('Environment variables')}>
                                        <Form.List name="env">
                                            {(fields, { add, remove }) => (
                                                <>
                                                    {fields.map((f) => (
                                                        <Space key={f.key} align="baseline" style={{ display: 'flex' }}>
                                                            <Form.Item name={[f.name, 'key']} rules={[{ required: true, pattern: /^[A-Za-z_][A-Za-z0-9_]*$/, message: t('Invalid variable name') }]}>
                                                                <Input placeholder="KEY" />
                                                            </Form.Item>
                                                            <Form.Item name={[f.name, 'value']}>
                                                                <Input placeholder="VALUE" />
                                                            </Form.Item>
                                                            <Button type="text" icon={<DeleteOutlined />} onClick={() => remove(f.name)} />
                                                        </Space>
                                                    ))}
                                                    <Button type="dashed" icon={<PlusOutlined />} onClick={() => add()}>{t('Add')}</Button>
                                                </>
                                            )}
                                        </Form.List>
                                    </Form.Item>
                                </>
                            ),
                        }]} />
                        <Button type="primary" htmlType="submit" loading={submitting}>{t('Buy now')}</Button>
                    </Form>
                </Card>
            )}
        </div>
    );
};

export default CreateContainer;
